import { Box, Divider, Skeleton, Typography } from "@mui/material";
import { useCampaignAnalytics } from "@/hooks/useCampaignAnalytics";
import type { Campaign } from "@/interfaces/campaing";
import TextButton from "../TextButton";

interface CampaignMetricCardProps {
  label: string;
  value: string | number;
  subLabel?: string;
  onSubLabelClick?: () => void;
  loading?: boolean;
}

function CampaignMetricCard({
  label,
  value,
  subLabel,
  onSubLabelClick,
  loading = false,
}: CampaignMetricCardProps) {
  return (
    <Box
      sx={{
        height: 140,
        bgcolor: "var(--color-clear-dust)",
        border: "1px solid var(--color-smoke)",
        borderRadius: "var(--radius-card)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          textAlign: "center",
          width: "100%",
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: "var(--color-dark-dust)",
            fontWeight: 700,
            letterSpacing: { xs: "0.8px", sm: "1.98px" },
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={60} height={48} />
        ) : (
          <Typography
            sx={{
              fontFamily: "var(--font-crimson)",
              fontSize: 42,
              fontWeight: 400,
              color: "var(--color-rodeo-black)",
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
        )}
        {subLabel ? (
          <TextButton onClick={onSubLabelClick}>{subLabel}</TextButton>
        ) : (
          <Box aria-hidden sx={{ visibility: "hidden" }}>
            <TextButton tabIndex={-1}>View All</TextButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}

interface CampaignAnalyticsPanelProps {
  campaign: Campaign;
  /** Absent when there is nothing to fix; the card then shows no "Fix Bounces" link. */
  onFixBounces?: () => void;
}

export default function CampaignAnalyticsPanel({
  campaign,
  onFixBounces,
}: CampaignAnalyticsPanelProps) {
  const { data: analytics, isLoading } = useCampaignAnalytics(campaign.id);

  const daysLeft = campaign.daysLeft ?? campaign.durationDays;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Divider sx={{ mx: -2.5 }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
          gap: 3,
        }}
      >
        <CampaignMetricCard
          label="Gifts Sent"
          value={analytics?.giftsSent ?? 0}
          subLabel={analytics?.sentCountIncomplete ? "Redeemed exceeds sent" : undefined}
          loading={isLoading}
        />
        <CampaignMetricCard
          label="Open Rate"
          value={`${analytics?.openRate ?? 0}%`}
          subLabel={analytics?.openedCountIncomplete ? "Redeemed exceeds opened" : undefined}
          loading={isLoading}
        />
        <CampaignMetricCard
          label="Gifts Redeemed"
          value={analytics?.giftsRedeemed ?? 0}
          loading={isLoading}
        />
        <CampaignMetricCard
          label="Redemption Rate"
          value={`${analytics?.redemptionRate ?? 0}%`}
          subLabel={analytics?.redemptionRateCapped ? "Redeemed exceeds sent" : undefined}
          loading={isLoading}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
          gap: 3,
        }}
      >
        <CampaignMetricCard
          label="Charity Rate"
          value={`${analytics?.charityRate ?? 0}%`}
          loading={isLoading}
        />
        <CampaignMetricCard
          label="Thank You Notes"
          value={analytics?.thankYouNotes ?? 0}
          subLabel="View All"
          loading={isLoading}
        />
        <CampaignMetricCard
          label="Delivered"
          value={`${analytics?.deliveredRate ?? 0}%`}
          subLabel={onFixBounces ? "Fix Bounces" : undefined}
          onSubLabelClick={onFixBounces}
          loading={isLoading}
        />
        <CampaignMetricCard label="Days Left" value={daysLeft} />
      </Box>
    </Box>
  );
}
